import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DbExplorerStateAndActions,
  useDbExplorerStore,
} from "../../stores/dbExplorerStore";
import { Database, Download } from "lucide-react";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";

export function DatabaseHeader() {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportRowCount, setExportRowCount] = useState<string>("1000");

  const {
    tables,
    selectedTable,
    tablesLoading,
    setSelectedTable,
    availableAgents,
    selectedAgentId,
    setSelectedAgentConnection,
    integrationsLoading,
    pageSize,
    setPageSize,
    exportCSV,
    isConnectionStringSet,
  } = useDbExplorerStore(
    useShallow((state: DbExplorerStateAndActions) => ({
      tables: state.tables,
      selectedTable: state.selectedTable,
      tablesLoading: state.tablesLoading,
      setSelectedTable: state.setSelectedTable,
      availableAgents: state.availableAgents,
      selectedAgentId: state.selectedAgentId,
      setSelectedAgentConnection: state.setSelectedAgentConnection,
      integrationsLoading: state.integrationsLoading,
      pageSize: state.pageSize,
      setPageSize: state.setPageSize,
      exportCSV: state.exportCSV,
      isConnectionStringSet: state.isConnectionStringSet,
    })),
  );

  const handleExportCSV = () => {
    const rowCount = exportRowCount.trim() === "" ? 0 : parseInt(exportRowCount);
    if (isNaN(rowCount) || rowCount < 0) return;
    exportCSV(rowCount);
    setIsExportDialogOpen(false);
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        {availableAgents.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600">Database:</span>
            <Select
              value={selectedAgentId || undefined}
              onValueChange={(value) => void setSelectedAgentConnection(value)}
              disabled={integrationsLoading}
            >
              <SelectTrigger className="h-9 w-[240px] border-slate-200/60 bg-white/80 hover:bg-slate-50/80 focus:ring-2 focus:ring-emerald-500/20">
                <SelectValue
                  placeholder={
                    integrationsLoading ? "Loading integrations..." : "Select connected database"
                  }
                />
              </SelectTrigger>
              <SelectContent className="border-slate-200/60">
                {availableAgents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id} className="hover:bg-slate-50/80">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-800">{agent.name}</div>
                        {agent.description ? (
                          <div className="truncate text-xs text-slate-500">
                            {agent.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isConnectionStringSet && (
          <div className="flex items-center gap-2 pl-2 border-l border-slate-200/60">
            <span className="text-xs text-slate-600">Table:</span>
            <Select value={selectedTable} onValueChange={setSelectedTable}>
              <SelectTrigger className="w-[220px] h-9 border-slate-200/60 bg-white/80 hover:bg-slate-50/80 focus:ring-2 focus:ring-green-500/20">
                <SelectValue
                  placeholder={tablesLoading ? "Loading tables..." : "Select table"}
                />
              </SelectTrigger>
              <SelectContent className="border-slate-200/60">
                {tablesLoading ? (
                  <div className="px-2 py-1 text-sm text-slate-600">Loading tables...</div>
                ) : (
                  tables.map((table) => (
                    <SelectItem
                      key={table.table_name}
                      value={table.table_name}
                      className="hover:bg-slate-50/80"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="font-medium text-slate-800">{table.table_name}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedTable && (
          <div className="flex items-center space-x-3 pl-2 border-l border-slate-200/60">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-slate-600">Rows:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => setPageSize(parseInt(value))}
              >
                <SelectTrigger className="w-[110px] border-slate-200/60 bg-white/80 hover:bg-slate-50/80">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-200/60">
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2 border-slate-200/60 bg-white/80 hover:bg-slate-50/80 text-slate-700 font-medium transition-all duration-200"
                >
                  <Download className="h-4 w-4" />
                  <span>Export CSV</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] border-slate-200/60">
                <DialogHeader className="space-y-3">
                  <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                    Export CSV
                  </DialogTitle>
                  <DialogDescription className="text-slate-600">
                    Specify how many rows you want to export to CSV. Leave empty or enter 0 to
                    export all rows.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label htmlFor="row-count" className="text-sm font-medium text-slate-700">
                      Number of rows
                    </label>
                    <Input
                      id="row-count"
                      type="number"
                      min="0"
                      value={exportRowCount}
                      onChange={(e) => setExportRowCount(e.target.value)}
                      placeholder="Enter number of rows (0 for all)"
                      className="border-slate-200/60"
                    />
                    <p className="text-xs text-slate-500">
                      Current filters and sorting will be applied to the export.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsExportDialogOpen(false)}
                      className="border-slate-200/60"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleExportCSV}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {!isConnectionStringSet && (
          <div className="flex items-center gap-2 text-slate-400">
            <Database className="h-4 w-4" />
            <span className="text-sm">No connection</span>
          </div>
        )}
      </div>
    </div>
  );
}
